import React from 'react';
import VALUES from './valuesData';

// Value Card Component
const ValueCard = ({ icon, title, description }) => (
    <div className="flex space-x-4 p-4 rounded-lg transition-all duration-300 hover:bg-white/5 relative">

        <div className="flex-shrink-0 bg-gradient-to-tr from-slate-800 to-slate-700 p-3 rounded-lg border border-slate-600/30 shadow-lg relative">
            {/* Data indicator */}
            <div className="absolute -right-1 -top-1 w-2 h-2 bg-blue-400 rounded-full"></div>
            {icon}
        </div>
        <div>
            <h3 className="text-lg font-semibold text-white mb-2">{title}</h3>
            <p className="text-slate-300 text-sm">{description}</p>

            {/* Horizontal data line */}
            <div className="mt-3 h-1 bg-slate-700 rounded-full w-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-blue-500 to-cyan-500 w-2/3 rounded-full"></div>
            </div>
        </div>
    </div>
);

// Process Step Component with pipeline connection
const ProcessStep = ({ number, title, description, color }) => (
    <li className="flex items-start relative">
        {/* Vertical connector between steps */}
        <div className="absolute left-5 top-10 bottom-0 w-1 bg-blue-500/20"></div>

        <div className={`flex-shrink-0 h-10 w-10 rounded-full ${color} flex items-center justify-center mr-4 text-white font-bold relative z-10`}>
            {number}
        </div>
        <div>
            <h3 className="text-lg font-semibold text-white mb-1">{title}</h3>
            <p className="text-slate-300 text-sm">{description}</p>

            {/* Data flow indicator */}
            <div className="mt-2 flex items-center">
                <div className="w-12 h-1 bg-slate-700 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-blue-500 to-purple-500 w-full rounded-full"></div>
                </div>
                <div className="w-2 h-2 bg-purple-400 rounded-full ml-1"></div>
            </div>
        </div>
    </li>
);

const MissionSection = () => {
    return (
        <section className="py-16 px-4 relative z-10 bg-slate-800/30">
            <div className="max-w-6xl mx-auto">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                    <div>
                        <h2 className="text-3xl font-bold mb-6 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">Our Mission</h2>
                        <p className="text-lg text-slate-300 mb-4 leading-relaxed">
                            Our mission is to democratize data analytics, making powerful business intelligence
                            accessible to organizations of all sizes. We believe that data-driven decision making
                            should be at the core of every modern business.
                        </p>
                        <p className="text-lg text-slate-300 mb-8 leading-relaxed">
                            We're committed to helping our clients transform raw information into strategic insights,
                            enabling them to identify opportunities, mitigate risks, and gain competitive advantage
                            in their respective industries.
                        </p>

                        <h2 className="text-3xl font-bold mb-6 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">Our Values</h2>
                        <div className="space-y-4">
                            {VALUES.map((value, index) => (
                                <ValueCard key={index} {...value} />
                            ))}
                        </div>
                    </div>

                    <div className="relative">
                        <div className="relative overflow-hidden rounded-xl bg-gradient-to-b from-slate-800/70 to-slate-900/70 border border-slate-700/50 shadow-xl">
                            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 to-purple-500"></div>
                            <div className="p-8">
                                <h2 className="text-2xl font-bold mb-8 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">Our Approach</h2>
                                <ol className="space-y-6">
                                    <ProcessStep
                                        number="1"
                                        title="Discover"
                                        description="We begin by understanding your business objectives and data landscape to identify key opportunities."
                                        color="bg-blue-500/80"
                                    />
                                    <ProcessStep
                                        number="2"
                                        title="Design"
                                        description="Our experts craft a tailored solution architecture that addresses your specific needs and challenges."
                                        color="bg-purple-500/80"
                                    />
                                    <ProcessStep
                                        number="3"
                                        title="Develop"
                                        description="We build and implement robust, scalable solutions using cutting-edge technologies."
                                        color="bg-green-500/80"
                                    />
                                    <ProcessStep
                                        number="4"
                                        title="Deploy"
                                        description="We ensure smooth implementation with comprehensive training and support for your team."
                                        color="bg-amber-500/80"
                                    />
                                </ol>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default MissionSection;